/**
 * Layout component that queries for data
 * with Gatsby's StaticQuery component
 *
 * See: https://www.gatsbyjs.org/docs/static-query/
 */

import React, { useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import toReact from 'svelte-adapter/react'
import GoogleAnalytics from 'react-ga'
import Dialogs from 'webkit/ui/Dialog/Dialogs.svelte'
import { track } from 'webkit/analytics'
import { initTwitterPixel } from 'webkit/analytics/twitter'
import { newHeadScript } from 'webkit/analytics/utils'
import { trackLink } from 'webkit/analytics/events/general'
import { startResponsiveController } from 'webkit/responsive'
import { useExperiment } from '@marvelapp/react-ab-test'
import { MixpanelContext } from '../apollo/wrap-root-element'
import StripeProviderSSR from './StripeProviderSSR'
import Intercom from './Intercom'
import Header from './Header/Header'
import Footer from './Footer/Footer'
import Notifications from './Notifications/Notifications'
import CookiePopup from './CookiePopup/CookiePopup'
import TopPanel from './TopPanel'
import BannerComponent from './Banner/index.svelte'
import styles from './layout.module.scss'
import DialogsWithCtx from './DialogsWithCtx.svelte'
import 'webkit/styles/main.css'

const Banner = toReact(BannerComponent, {}, 'div')

if (process.browser) {
  startResponsiveController()
  newHeadScript(`!function(){"use strict";!function(e,t){var n=e.amplitude||{_q:[],_iq:{}};if(n.invoked)e.console&&console.error&&console.error("Amplitude snippet has been loaded.");else{var r=function(e,t){e.prototype[t]=function(){return this._q.push({name:t,args:Array.prototype.slice.call(arguments,0)}),this}},s=function(e,t,n){return function(r){e._q.push({name:t,args:Array.prototype.slice.call(n,0),resolve:r})}},o=function(e,t,n){e[t]=function(){if(n)return{promise:new Promise(s(e,t,Array.prototype.slice.call(arguments)))}}},i=function(e){for(var t=0;t<m.length;t++)o(e,m[t],!1);for(var n=0;n<g.length;n++)o(e,g[n],!0)};n.invoked=!0;var u=t.createElement("script");u.type="text/javascript",u.integrity="sha384-x0ik2D45ZDEEEpYpEuDpmj05fY91P7EOZkgdKmq4dKL/ZAVcufJ+nULFtGn0HIZE",u.crossOrigin="anonymous",u.async=!0,u.src="https://cdn.amplitude.com/libs/analytics-browser-2.0.0-min.js.gz",u.onload=function(){e.amplitude.runQueuedFunctions||console.log("[Amplitude] Error: could not load SDK")};var a=t.getElementsByTagName("script")[0];a.parentNode.insertBefore(u,a);for(var c=function(){return this._q=[],this},p=["add","append","clearAll","prepend","set","setOnce","unset","preInsert","postInsert","remove","getUserProperties"],l=0;l<p.length;l++)r(c,p[l]);n.Identify=c;for(var d=function(){return this._q=[],this},f=["getEventProperties","setProductId","setQuantity","setPrice","setRevenue","setRevenueType","setEventProperties"],v=0;v<f.length;v++)r(d,f[v]);n.Revenue=d;var m=["getDeviceId","setDeviceId","getSessionId","setSessionId","getUserId","setUserId","setOptOut","setTransport","reset","extendSession"],g=["init","add","remove","track","logEvent","identify","groupIdentify","setGroup","revenue","flush"];i(n),n.createInstance=function(e){return n._iq[e]={_q:[]},i(n._iq[e]),n._iq[e]},e.amplitude=n}}(window,document)}();

amplitude.init('b02993b26b4c6899b56fc40566612e97', undefined, {
    flushIntervalMillis: 1,
    flushQueueSize: 1
});`)

  const root = document.documentElement

  root.addEventListener('click', e => {
    // Adapted from https://github.com/visionmedia/page.js
    // MIT license https://github.com/visionmedia/page.js#license
    const { button, metaKey, ctrlKey, shiftKey, altKey, defaultPrevented } = e
    if (button) return
    if (metaKey || ctrlKey || shiftKey || altKey) return
    if (defaultPrevented) return

    const target = e.target
    if (!target) return

    const anchor = target.closest('a')
    if (!anchor) return

    const href = anchor.getAttribute('href') || ''
    const isExternal = href.startsWith('http')
    const { type, source } = anchor.dataset

    if (isExternal) {
      e.preventDefault()
      window.open(href, '_blank').focus()
    }

    trackLink({ url: href, external: isExternal, type, source })
  })
}

if (process.env.NODE_ENV === 'production') {
  GoogleAnalytics.initialize('UA-100571693-8')

  if (typeof window !== 'undefined') {
    initTwitterPixel()
  }
} else {
  GoogleAnalytics.initialize('UA-100571693-8', { testMode: true })
}

const Layout = ({ children, isAccountPage, classes = {} }) => {
  const mixpanel = useContext(MixpanelContext)
  const { selectVariant, emitWin, activeVariant } = useExperiment(
    'SanApi Questionnaire'
  )

  const variant = selectVariant({
    Questionnaire: (
      <>
        <TopPanel />
        <Banner />
      </>
    ),
    Old: <></>,
  })

  useEffect(() => {
    track.pageview('api.santiment.net')
    emitWin('SanApi Questionnaire')
    // TODO: Uncomment after prod test
    // mixpanel.track('SanApi Questionnaire' + ' ' + activeVariant, {
    //   name: 'SanApi Questionnaire',
    //   variant: activeVariant,
    // })

    const svelte = new DialogsWithCtx({ target: document.body })
    return () => svelte.$destroy()
  }, [])

  return (
    <StripeProviderSSR>
      <Intercom>
        <Notifications>
          <div className={styles.container}>
            {variant}
            <Header isAccountPage={isAccountPage} />
            <main className={cx(styles.main, classes.main)}>{children}</main>
            <Footer />
          </div>
        </Notifications>
        <CookiePopup />
      </Intercom>
    </StripeProviderSSR>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
}

export default Layout
